<template>
    <div class="Visualization">
        <div
            id="view-wrapper"
            class="Wrapper flex items-stretch"
        >
            <div class="ChartContainer flex-1">
                <div
                    class="Actions flex row-stretch-center"
                >
                    <div
                        v-if="chart"
                        class="flex row-stretch-center"
                    >
                        <!-- Exportar imagen -->
                        <QBtnDropdown
                            dense
                            autoClose
                            flat
                            color="white"
                            textColor="primary"
                            class="q-mr-sm"
                        >
                            <template #label>
                                <QIcon name="collections" color="primary" />
                                <QTooltip>
                                    {{ $t('visualization.exportImageFormat') }}
                                </QTooltip>
                            </template>
                            <QList dense class="text-caption">
                                <QItem
                                    v-for="(action, idx) in exportOptions"
                                    :key="idx"
                                    clickable
                                    @click="handleExportImage(action)"
                                >
                                    <QItemSection>
                                        {{ action.label }}
                                    </QItemSection>
                                </QItem>
                            </QList>
                        </QBtnDropdown>
                    </div>
                </div>
                <div class="GraphArea">
                    <div
                        v-if="(settedChart && !variantsLoading) && (!chart || !(chart.activities || []).length || !(Object.keys(chart) || []).length)"
                        class="EmptyModel"
                    >
                        {{ $t('visualization.filters.emptyModel') }}
                    </div>
                    <GraphCyTo
                        v-else
                        ref="chart"
                        class="Graph"
                        :data="chart"
                        :model="model"
                        :color="color"
                        :variantsLoading="variantsLoading"
                        :settedChart="settedChart"
                        :tab="tab"
                        parent="view-wrapper"
                        @frequency-indicator-state-changed="handleFrequencyIndicatorStateChanged"
                        @zoom-state-changed="handleZoomStateChanged"
                    />
                    <ZoomControl
                        v-if="chart"
                        class="ZoomControlsOverlay"
                        :canIncrease="canIncreaseZoom"
                        :canDecrease="canDecreaseZoom"
                        @increase="handleIncreaseZoom"
                        @decrease="handleDecreaseZoom"
                        @center-chart="handleCenterChart"
                    />
                </div>

                <div
                    v-if="tab === 'MODEL' || tab === 'LOOPS'"
                    ref="panelsContainer"
                    class="Inspector"
                >
                    <div class="InspectorBar row items-center justify-between no-wrap">
                        <div class="row items-center no-wrap">
                            <QIcon
                                name="tune"
                                size="18px"
                                color="primary"
                                class="q-mr-sm"
                            />

                            <div
                                class="InspectorCompactGroup"
                                :class="{ 'is-disabled': !hasInspectorInfo }"
                                :tabindex="hasInspectorInfo ? 0 : -1"
                                @click="hasInspectorInfo && openInspectorTab('MODEL')"
                                @keydown.enter.prevent="hasInspectorInfo && openInspectorTab('MODEL')"
                                @keydown.space.prevent="hasInspectorInfo && openInspectorTab('MODEL')"
                            >
                                <div class="text-caption text-weight-medium">
                                    {{ $t('visualization.modelMetrics.title') }}
                                </div>
                                <div class="text-caption q-ml-sm text-grey-7 row items-center no-wrap">
                                    <span>
                                        {{ $t('visualization.filters.contextualInfo.cases') }}: {{ compactCasesValue }}
                                    </span>
                                    <QBadge
                                        v-if="compactCasesPct !== null"
                                        class="q-ml-xs"
                                        color="secondary"
                                        textColor="white"
                                        :label="`${compactCasesPct}%`"
                                    >
                                            <QTooltip class="bg-gray-bg text-primary">
                                            {{ compactPctTooltip }}
                                        </QTooltip>
                                    </QBadge>

                                    <span class="q-ml-sm">
                                        {{ $t('visualization.filters.contextualInfo.variants') }}: {{ compactVariantsValue }}
                                    </span>
                                    <QBadge
                                        v-if="compactVariantsPct !== null"
                                        class="q-ml-xs"
                                        color="secondary"
                                        textColor="white"
                                        :label="`${compactVariantsPct}%`"
                                    >
                                            <QTooltip class="bg-gray-bg text-primary">
                                            {{ compactPctTooltip }}
                                        </QTooltip>
                                    </QBadge>

                                    <span class="q-ml-sm">
                                        {{ $t('visualization.filters.contextualInfo.avg') }}: {{ compactAvgDuration }}
                                    </span>
                                </div>
                            </div>

                            <q-separator vertical class="q-mx-sm" />
                            <div
                                class="InspectorCompactGroup"
                                :class="{ 'is-disabled': !hasCompactSlaSummary }"
                                :tabindex="hasCompactSlaSummary ? 0 : -1"
                                @click="hasCompactSlaSummary && openInspectorTab('SLA')"
                                @keydown.enter.prevent="hasCompactSlaSummary && openInspectorTab('SLA')"
                                @keydown.space.prevent="hasCompactSlaSummary && openInspectorTab('SLA')"
                            >
                                <div class="text-caption text-weight-medium">
                                    {{ $t('visualization.SLAInfo.compact.caseTargetDurationTitle') }}
                                    <QTooltip
                                        v-if="compactSlaTargetDurationRaw"
                                        class="bg-gray-bg text-primary"
                                    >
                                        {{ $t('visualization.SLAInfo.targetDuration') }}: {{ compactSlaTargetDurationRaw }}
                                    </QTooltip>
                                </div>
                                <div class="text-caption q-ml-sm text-grey-7 row items-center no-wrap">
                                    <QBadge
                                        color="red-11"
                                        textColor="white"
                                        :label="compactSlaNonCompliantLabel"
                                    >
                                        <QTooltip class="bg-gray-bg text-primary">
                                            {{ $t('visualization.SLAInfo.nonCompliantCases') }}
                                        </QTooltip>
                                    </QBadge>
                                    <span class="q-mx-xs">/</span>
                                    <QBadge
                                        color="green-12"
                                        textColor="white"
                                        :label="compactSlaCompliantLabel"
                                    >
                                        <QTooltip class="bg-gray-bg text-primary">
                                            {{ $t('visualization.SLAInfo.compliantCases') }}
                                        </QTooltip>
                                    </QBadge>
                                    <QBadge
                                        v-if="compactSlaCompliantPct !== null"
                                        class="q-ml-sm"
                                        color="secondary"
                                        textColor="white"
                                        :label="`${compactSlaCompliantPct}%`"
                                    >
                                        <QTooltip class="bg-gray-bg text-primary">
                                            {{ $t('visualization.SLAInfo.compliantPct') }}
                                        </QTooltip>
                                    </QBadge>
                                </div>
                            </div>

                            <template v-if="tab === 'LOOPS'">
                                <q-separator vertical class="q-mx-sm" />
                                <div
                                    class="InspectorCompactGroup"
                                    :class="{ 'is-disabled': !hasInspectorInfo }"
                                    :tabindex="hasInspectorInfo ? 0 : -1"
                                    @click="hasInspectorInfo && openInspectorTab('LOOP')"
                                    @keydown.enter.prevent="hasInspectorInfo && openInspectorTab('LOOP')"
                                    @keydown.space.prevent="hasInspectorInfo && openInspectorTab('LOOP')"
                                >
                                    <div class="text-caption text-weight-medium">
                                        {{ $t('visualization.loopMetrics.title') }}
                                    </div>
                                    <div class="text-caption q-ml-sm text-grey-7 row items-center no-wrap">
                                        <span>
                                            {{ $t('visualization.loopMetrics.avgTime') }}: {{ compactLoopAvgDuration }}
                                        </span>
                                        <QBadge
                                            v-if="compactLoopPct !== null"
                                            class="q-ml-xs"
                                            color="secondary"
                                            textColor="white"
                                            :label="`${compactLoopPct}%`"
                                        >
                                            <QTooltip class="bg-gray-bg text-primary">
                                                {{ $t('visualization.loopMetrics.percentTime') }}
                                            </QTooltip>
                                        </QBadge>

                                        <span class="q-ml-sm">
                                            {{ $t('visualization.loopMetrics.avgCost') }}: {{ compactLoopAvgCost }}
                                        </span>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <div class="row items-center no-wrap">
                            <QBtn
                                dense
                                flat
                                color="primary"
                                :disable="!hasInspectorInfo && !hasInspectorPlots"
                                :icon="inspectorOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_up'"
                                @click="toggleInspector"
                            >
                                <QTooltip>
                                    {{ inspectorOpen ? $t('visualization.filters.contextualInfo.toggleInspectorToolsHide') : $t('visualization.filters.contextualInfo.toggleInspectorToolsShow') }}
                                </QTooltip>
                            </QBtn>
                        </div>
                    </div>
                    <QSeparator />
                    <QSlideTransition>
                        <div
                            v-show="inspectorOpen"
                            class="InspectorBody"
                        >
                            <QTabs
                                v-model="inspectorTab"
                                dense
                                align="left"
                                activeColor="primary"
                                indicatorColor="primary"
                            >
                                <QTab
                                    name="MODEL"
                                    :disable="!hasInspectorInfo"
                                    icon="info"
                                >
                                    <QTooltip class="bg-gray-bg text-primary">
                                        {{ $t('visualization.modelMetrics.title') }}
                                    </QTooltip>
                                </QTab>
                                <QTab
                                    name="SLA"
                                    icon="schedule"
                                >
                                    <QTooltip class="bg-gray-bg text-primary">
                                        {{ $t('visualization.SLAInfo.title') }}
                                    </QTooltip>
                                </QTab>
                                <QTab
                                    name="LOOP"
                                    :disable="tab !== 'LOOPS' || !hasInspectorInfo"
                                    icon="360"
                                >
                                    <QTooltip class="bg-gray-bg text-primary">
                                        {{ $t('visualization.loopMetrics.title') }}
                                    </QTooltip>
                                </QTab>
                                <QTab
                                    name="ATTRIBUTE"
                                    :disable="!hasInspectorPlots"
                                    icon="query_stats"
                                >
                                    <QTooltip class="bg-gray-bg text-primary">
                                        {{ $t('visualization.customPlotPanels.attributeInfo') }}
                                    </QTooltip>
                                </QTab>
                                <QTab
                                    name="FREQANDDUR"
                                    :disable="!hasInspectorPlots"
                                    icon="timelapse"
                                >
                                    <QTooltip class="bg-gray-bg text-primary">
                                        {{ $t('visualization.customPlotPanels.frequencyAndDuration') }}
                                    </QTooltip>
                                </QTab>
                                <QTab
                                    name="DURATIONMETER"
                                    :disable="!hasInspectorPlots"
                                    icon="av_timer"
                                >
                                    <QTooltip class="bg-gray-bg text-primary">
                                        {{ $t('visualization.customPlotPanels.durationMeter') }}
                                    </QTooltip>
                                </QTab>
                            </QTabs>
                            <QSeparator />
                            <QTabPanels
                                v-model="inspectorTab"
                                animated
                            >
                                <QTabPanel
                                    name="MODEL"
                                    class="q-pa-none"
                                >
                                    <ModelMetricsDocked
                                        v-if="hasInspectorInfo"
                                        :totalInfo="infoTotal"
                                        :filteredInfo="infoFiltered"
                                        :displayedInfo="displayedInfo"
                                        :percentageMode="percentageDisplay.value"
                                    />
                                </QTabPanel>
                                <QTabPanel
                                    name="SLA"
                                    class="q-pa-none"
                                >
                                    <SLAInfoDocked
                                        v-if="hasInspectorInfo"
                                        :variantTab="variantTab"
                                        :info="displayedInfo"
                                        :chart="chart"
                                        :variantsLoading="disableElements"
                                    />
                                </QTabPanel>

                                <QTabPanel
                                    name="LOOP"
                                    class="q-pa-none"
                                >
                                    <LoopMetricsDocked
                                        v-if="tab === 'LOOPS' && hasInspectorInfo"
                                        :displayedInfo="displayedInfo"
                                        :chartInfo="chart"
                                        :currency="currency"
                                    />
                                </QTabPanel>

                                <QTabPanel
                                    name="ATTRIBUTE"
                                    class="q-pa-none"
                                >
                                    <q-card flat bordered>
                                        <q-card-section class="q-pa-none">
                                            <AttributeInformationPanel
                                                v-if="hasInspectorPlots"
                                                :attributes="attributes"
                                                :categoricalAttributes="categoricalAttributes"
                                                :numericAttributes="numericAttributes"
                                                :processId="processId"
                                                :filters="formatFiltersParam(visualizationFilters)"
                                                :chart="chart"
                                            />
                                        </q-card-section>
                                    </q-card>
                                </QTabPanel>

                                <QTabPanel
                                    name="FREQANDDUR"
                                    class="q-pa-none"
                                >
                                    <q-card flat bordered>
                                        <q-card-section class="q-pa-none">
                                            <FrequencyDurationPanelDocked
                                                v-if="hasInspectorPlots"
                                                :processId="processId"
                                                :filters="formatFiltersParam(visualizationFilters)"
                                                :chart="chart"
                                            />
                                        </q-card-section>
                                    </q-card>
                                </QTabPanel>

                                <QTabPanel
                                    name="DURATIONMETER"
                                    class="q-pa-none"
                                >
                                    <q-card flat bordered>
                                        <q-card-section class="q-pa-none">
                                            <DurationMeterPanel
                                                v-if="hasInspectorPlots"
                                                :processId="processId"
                                                :filters="formatFiltersParam(visualizationFilters)"
                                                :chart="chart"
                                            />
                                        </q-card-section>
                                    </q-card>
                                </QTabPanel>
                            </QTabPanels>
                        </div>
                    </QSlideTransition>
                </div>
            </div>
        </div>
        <CreateModel
            ref="modelModal"
            @save="generateModel"
        />
        <LoadingSpinner
            :visible="isExportingFile"
            :format="format"
        />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { filtersStorageMixin } from '@/mixins'
import {
    Api, apiRequest, notifyError, notifySuccess,
} from '@/api'
import LoadingSpinner from './components/LoadingSpinner.vue'
import CreateModel from './components/CreateModel.vue'
import ModelMetricsDocked from './components/Contextualnfo/ModelMetricsDocked.vue'
import LoopMetricsDocked from './components/Contextualnfo/LoopMetricsDocked.vue'
import SLAInfoDocked from './components/Contextualnfo/SLAInfoDocked.vue'
import AttributeInformationPanel from './components/Contextualnfo/AttributeInformationPanel.vue'
import FrequencyDurationPanelDocked from './components/Contextualnfo/FrequencyDurationPanelDocked.vue'
import DurationMeterPanel from './components/Contextualnfo/DurationMeterPanel.vue'
import GraphCyTo from '../../../../components/Graph/GraphCyTo.vue'
import ZoomControl from './components/ZoomControl.vue'

const DEFAULT_PERCENTAGE_MODE = {
    label: 'visualization.percentageTotal',
    value: 'TOTAL',
    icon: 'storage',
}
const DEFAULT_CURRENCY = {
    value: 'euro',
    label: 'Euro (€)',
}
const DEFAULT_COST_PER_HOUR = 0
const DEFAULT_HEATMAP_MODE = 'OFF'
const INITIAL_MODEL = {
    variant: 1,
    variants: 1,
    directionValue: 'LR',
    statsValue: 'averages',
    values: 'both',
    heatmapMode: DEFAULT_HEATMAP_MODE,
}
const EMPTY_MODEL_INFO = {
    activities: undefined,
    cases: undefined,
    duration: undefined,
    maxEndDateTime: undefined,
    minStartDateTime: undefined,
    variants: undefined,
}
const DIRECTION_ICONS = {
    horizontal: 'east',
    vertical: 'south',
}
const DEFAULT_DIRECTION = {
    value: 'LR',
    icon: DIRECTION_ICONS.horizontal,
}
const DEFAULT_TAB = 'MODEL'
const DEFAULT_VARIANT_TAB = 'FREQUENCY'

export default {
    name: 'VisualizationComponent',
    components: {
        GraphCyTo,
        CreateModel,
        LoadingSpinner,
        ModelMetricsDocked,
        SLAInfoDocked,
        LoopMetricsDocked,
        ZoomControl,
        AttributeInformationPanel,
        FrequencyDurationPanelDocked,
        DurationMeterPanel,
    },
    mixins: [filtersStorageMixin],
    props: {
        processId: VueTypes.any,
        categoricalAttributes: VueTypes.array,
        numericAttributes: VueTypes.array,
        attributes: VueTypes.array,
        infoTotal: VueTypes.object,
        infoFiltered: VueTypes.object,
        forcedTab: VueTypes.string,
    },
    emits: ['display-state-changed', 'frequency-indicator-state-changed'],
    data() {
        return {
            tab: this.forcedTab || this.$route.params.tab || DEFAULT_TAB,
            panelsContainerObserver: undefined,
            resizeTimeout: null,
            resizeChartTimeout: null,
            chart: undefined,
            color: undefined,
            settedChart: false,
            disableElements: false,
            variantsLoading: false,
            variantTab: DEFAULT_VARIANT_TAB,
            model: INITIAL_MODEL,
            visibleOptions: false,
            imageTemp: undefined,
            info: undefined,
            displayedInfo: undefined,
            filteredTraces: 0,
            percentageDisplay: DEFAULT_PERCENTAGE_MODE,
            directionDisplay: DEFAULT_DIRECTION,
            statsDisplay: {
                abbreviation: 'avg',
                value: 'averages',
                label: this.$t('visualization.filters.contextualInfo.avg'),
            },
            heatmapModeDisplay: DEFAULT_HEATMAP_MODE,
            toggleLoopMetrics: true,
            currency: DEFAULT_CURRENCY,
            costPerHour: DEFAULT_COST_PER_HOUR,
            settings: undefined,
            loading: true,
            showPredictions: true,
            comparationFilters: [],
            currentFilters: [],
            attributePanelOption: { key: 'AI', label: this.$t('visualization.customPlotPanels.attributeInfo'), icon: 'query_stats' },
            frequencyPanelOption: { key: 'FD', label: this.$t('visualization.customPlotPanels.frequencyAndDuration'), icon: 'timelapse' },
            activeFab: null,
            isExportingFile: false,
            format: 'PNG',
            fabs: ['percentageFab', 'directionFab', 'statsFab', 'exportImageFab'],
            frequencyIndicatorState: {
                visible: false,
                value: 100,
                minValue: 0,
                canIncrease: false,
                canDecrease: false,
            },
            tabKeys: {
                model: 0,
                loops: 0,
                conformance: 0,
                attrs: 0,
                dashboard: 0,
                comparison: 0,
                traces: 0,
            },
            inspectorOpen: false,
            inspectorTab: 'MODEL',
            zoomValue: 100,
            canIncreaseZoom: false,
            canDecreaseZoom: false,
        }
    },
    computed: {
        percOptions() {
            return [
                {
                    label: 'visualization.percentageTotal',
                    value: 'TOTAL',
                    icon: 'storage',
                },
                {
                    label: 'visualization.percentageFiltered',
                    value: 'FILTERED',
                    icon: 'filter_alt',
                },
            ]
        },
        curOptions() {
            return [
                {
                    label: 'Euro (€)',
                    value: 'euro',
                },
                {
                    label: 'Dollar ($)',
                    value: 'dollar',
                },
                {
                    label: 'Pound (£)',
                    value: 'pound',
                },
            ]
        },
        directionOptions() {
            return [
                {
                    value: 'LR',
                    icon: DIRECTION_ICONS.horizontal,
                },
                {
                    value: 'TB',
                    icon: DIRECTION_ICONS.vertical,
                },
            ]
        },
        statsOptions() {
            return [
                {
                    abbreviation: 'sd',
                    value: 'variances',
                    label: this.$t('visualization.filters.contextualInfo.sd'),
                },
                {
                    abbreviation: 'avg',
                    value: 'averages',
                    label: this.$t('visualization.filters.contextualInfo.avg'),
                },
                {
                    abbreviation: 'median',
                    value: 'median',
                    label: this.$t('visualization.filters.contextualInfo.med'),
                },
                {
                    abbreviation: 'min',
                    value: 'min',
                    label: this.$t('visualization.filters.contextualInfo.min'),
                },
                {
                    abbreviation: 'max',
                    value: 'max',
                    label: this.$t('visualization.filters.contextualInfo.max'),
                },
            ]
        },
        exportOptions() {
            return [
                {
                    value: 'PNG',
                    label: 'PNG',
                },
                {
                    value: 'JPG',
                    label: 'JPG',
                },
            ]
        },
        attributesNotEmpty() {
            return (this.attributes || []).length > 0
        },
        hasInspectorInfo() {
            return !!this.displayedInfo
        },
        hasInspectorPlots() {
            return !!(this.attributesNotEmpty && this.chart)
        },
        compactPctTooltip() {
            return this.percentageDisplay?.value === 'TOTAL'
                ? this.$t('visualization.filters.contextualInfo.total')
                : this.$t('visualization.filters.contextualInfo.filtered')
        },
        compactCasesValue() {
            const v = this.displayedInfo?.cases
            return v === undefined || v === null ? '-' : v
        },
        compactVariantsValue() {
            const v = this.displayedInfo?.variants
            return v === undefined || v === null ? '-' : v
        },
        compactCasesPct() {
            return this.computeCompactPct('cases')
        },
        compactVariantsPct() {
            return this.computeCompactPct('variants')
        },
        compactAvgDuration() {
            const seconds = this.displayedInfo?.duration?.avg
            return typeof seconds === 'number' ? this.formatSecondsCompact(seconds) : '-'
        },
        compactLoopAvgDuration() {
            const seconds = this.chart?.stats?.avgDuration
            return typeof seconds === 'number' ? this.formatSecondsCompact(seconds) : '-'
        },
        compactLoopPct() {
            const loopSeconds = this.chart?.stats?.avgDuration
            const caseSeconds = this.displayedInfo?.duration?.avg
            if (typeof loopSeconds !== 'number' || typeof caseSeconds !== 'number' || caseSeconds <= 0) return null
            return Math.round((loopSeconds / caseSeconds) * 100)
        },
        compactLoopAvgCost() {
            const avgCostRaw = this.chart?.stats?.avgCost
            return typeof avgCostRaw === 'number'
                ? `${Math.round(avgCostRaw)}${this.currencySymbol}`
                : '-'
        },
        compactSlaAllTarget() {
            const list = this.displayedInfo?.customSLA
            if (!Array.isArray(list)) return null
            return list.find((s) => s && s.variantId === 'all') || null
        },
        hasCompactSlaSummary() {
            return !!(this.hasInspectorInfo && typeof this.displayedInfo?.cases === 'number' && this.compactSlaAllTarget)
        },
        compactSlaNonCompliant() {
            if (!this.hasCompactSlaSummary) return null
            const c = this.compactSlaAllTarget?.count
            return typeof c === 'number' ? c : null
        },
        compactSlaCompliant() {
            if (!this.hasCompactSlaSummary) return null
            const total = this.displayedInfo?.cases
            const non = this.compactSlaNonCompliant
            if (typeof total !== 'number' || typeof non !== 'number') return null
            return Math.max(0, total - non)
        },
        compactSlaNonCompliantLabel() {
            return typeof this.compactSlaNonCompliant === 'number' ? `${this.compactSlaNonCompliant}` : '-'
        },
        compactSlaCompliantLabel() {
            return typeof this.compactSlaCompliant === 'number' ? `${this.compactSlaCompliant}` : '-'
        },
        compactSlaCompliantPct() {
            if (!this.hasCompactSlaSummary) return null
            const total = this.displayedInfo?.cases
            const compliant = this.compactSlaCompliant
            if (typeof total !== 'number' || total <= 0 || typeof compliant !== 'number') return null
            return Math.round((compliant / total) * 100)
        },
        compactSlaTargetDurationRaw() {
            if (!this.hasCompactSlaSummary) return null
            const value = this.compactSlaAllTarget?.value
            const unit = this.compactSlaAllTarget?.unit
            if (value === undefined || value === null || !unit) return null
            return `${value} ${unit}`
        },
        currencySymbol() {
            const map = {
                euro: '€',
                dollar: '$',
                pound: '£',
            }
            return map[this.currency?.value] || ''
        },
    },
    watch: {
        forcedTab: {
            handler(val) {
                if (!val) return
                if (val === this.tab) return
                this.tab = val
            },
            immediate: true,
        },
        tab(a, oldTab) {
            if (oldTab === 'MODEL' || oldTab === 'LOOPS') {
                if (this.panelsContainerObserver && this.$refs.panelsContainer) {
                    this.panelsContainerObserver.unobserve(this.$refs.panelsContainer)
                }
            }
            if (a === 'MODEL' || a === 'LOOPS') {
                this.panelsContainerObserver = new ResizeObserver(this.onResize)
                setTimeout(() => {
                    if (this.$refs.panelsContainer) {
                        this.panelsContainerObserver.observe(this.$refs.panelsContainer)
                    }
                }, 50)
            }

            if (a !== 'LOOPS' && this.inspectorTab === 'LOOP') {
                this.inspectorTab = 'MODEL'
            }
        },
        // eslint-disable-next-line func-names
        '$route.params.tab': function (newVal) {
            this.tab = newVal || DEFAULT_TAB
        },

        chart() {
            this.emitDisplayStateChanged()
        },
        hasInspectorInfo() {
            if (!this.hasInspectorInfo && ['MODEL', 'SLA', 'LOOP'].includes(this.inspectorTab)) {
                this.inspectorTab = this.hasInspectorPlots ? 'ATTRIBUTE' : 'MODEL'
            }
            if (!this.hasInspectorInfo && !this.hasInspectorPlots) {
                this.inspectorOpen = false
            }
        },
        hasInspectorPlots() {
            if (!this.hasInspectorPlots && ['ATTRIBUTE', 'FREQANDDUR', 'DURATIONMETER'].includes(this.inspectorTab)) {
                this.inspectorTab = this.hasInspectorInfo ? 'MODEL' : 'ATTRIBUTE'
            }
            if (!this.hasInspectorInfo && !this.hasInspectorPlots) {
                this.inspectorOpen = false
            }
        },
    },
    async mounted() {
        const { tab } = this.$route.params
        await this.getSettings()
        if (tab) {
            this.tab = tab
        }
        if (this.forcedTab) {
            this.tab = this.forcedTab
        }
        if (this.tab === 'MODEL' || this.tab === 'LOOPS') {
            this.panelsContainerObserver = new ResizeObserver(this.onResize)
            setTimeout(() => {
                if (this.$refs.panelsContainer) {
                    this.panelsContainerObserver.observe(this.$refs.panelsContainer)
                }
            }, 50)
        }
        document.addEventListener('click', this.handleClickOutside)
    },
    created() {
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        formatSecondsCompact(seconds) {
            if (seconds === 0) return '0s'
            if (!seconds || typeof seconds !== 'number') return '-'

            const absSeconds = Math.abs(seconds)
            if (absSeconds < 60) return `${Math.round(seconds)}s`

            const minutes = Math.round(seconds / 60)
            if (Math.abs(minutes) < 60) return `${minutes}m`

            const hours = Math.round(minutes / 60)
            if (Math.abs(hours) < 48) return `${hours}h`

            const days = Math.round(hours / 24)
            return `${days}d`
        },
        computeCompactPct(key) {
            const shown = this.displayedInfo?.[key]
            if (typeof shown !== 'number') return null

            const denom = this.percentageDisplay?.value === 'TOTAL'
                ? this.infoTotal?.[key]
                : this.infoFiltered?.[key]

            if (typeof denom !== 'number' || denom <= 0) return null
            return Math.round((shown / denom) * 100)
        },
        emitDisplayStateChanged() {
            this.$emit('display-state-changed', this.getDisplayState())
        },
        emitFrequencyIndicatorStateChanged() {
            this.$emit('frequency-indicator-state-changed', this.getFrequencyIndicatorState())
        },
        getFrequencyIndicatorState() {
            return {
                visible: this.frequencyIndicatorState.visible,
                value: this.frequencyIndicatorState.value,
                minValue: this.frequencyIndicatorState.minValue,
                canIncrease: this.frequencyIndicatorState.canIncrease,
                canDecrease: this.frequencyIndicatorState.canDecrease,
            }
        },
        getDisplayState() {
            const hasChart = !!(this.chart && (this.chart.activities || []).length)
            return {
                percentageMode: this.percentageDisplay?.value,
                directionValue: this.directionDisplay?.value,
                statsValue: this.statsDisplay?.value,
                heatmapMode: this.model?.heatmapMode || this.heatmapModeDisplay,
                canSaveBpmn: hasChart,
            }
        },
        openSaveBpmn() {
            const hasChart = !!(this.chart && (this.chart.activities || []).length)
            if (!hasChart) return
            this.saveBpmnModel()
        },
        setPercentageModeValue(value) {
            const option = (this.percOptions || []).find((o) => o.value === value)
            if (!option) return
            this.setPercentageMode(option)
        },
        setDirectionValue(value) {
            const option = (this.directionOptions || []).find((o) => o.value === value)
            if (!option) return
            this.setDirectionMode(option)
        },
        setStatsValue(value) {
            const option = (this.statsOptions || []).find((o) => o.value === value)
            if (!option) return
            this.setStatsMode(option)
        },
        setHeatmapModeValue(value) {
            const allowed = ['OFF', 'FREQUENCY', 'DURATION']
            if (!allowed.includes(value)) return
            this.setHeatmapMode(value)
        },
        handleChangedVariant() {
            this.settedChart = false
        },
        onResize() {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout)
            }
            this.resizeTimeout = setTimeout(() => {
                // Panels moving/resizing can affect the available space; just refit the graph.
                const { chart } = this.$refs
                if (chart && typeof chart.scheduleFit === 'function') {
                    chart.scheduleFit()
                }
            }, 50)
        },
        handleRequestModelInfo(data) {
            this.getModelInfoDisplayed({ variantCount: data?.variants, variantUid: data?.variantId, loopUid: data?.loopId })
        },
        handleSetChart(data) {
            const formattedData = this.formatChartData(data)
            this.chart = data ? { ...formattedData } : undefined
            this.color = undefined
            this.emitDisplayStateChanged()
        },
        handleColorChartLoops(data) {
            if (data !== undefined) {
                const formattedData = this.formatChartData(data)
                this.color = data ? { ...formattedData } : undefined
            } else {
                this.color = undefined
            }
        },
        formatChartData(data = {}) {
            const activitiesIds = (data.activities || []).reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {})
            const arcs = (data.arcs || []).map(({ source, target, ...rest }) => ({
                ...rest,
                source: activitiesIds[source],
                target: activitiesIds[target],
                sourceId: source,
                targetId: target,
            }))
            return { ...data, arcs }
        },
        formatAttributes(attributes = {}) {
            return [...Object.keys(attributes)].map((key) => ({ label: key, value: key }))
        },
        handleCenterChart() {
            const { chart } = this.$refs
            if (chart) chart.centerGraph()
        },
        handleChangeTab(tab) {
            const { tab: actualTab, ...otherParams } = this.$route.params
            if (tab === actualTab) return
            const params = { tab, ...otherParams }
            if (tab) {
                this.$router.replace({ ...this.$route, params }).catch(() => {})
            }
            this.handleResetModel()
        },
        handleResetModel() {
            this.model = { ...INITIAL_MODEL }
            this.model.statsValue = this.statsDisplay.value
            this.model.heatmapMode = this.heatmapModeDisplay
        },
        reloadCurrentView() {
            if (this.tab === 'TRACES') {
                this.tabKeys.traces += 1
            } else if (this.tab === 'MODEL') {
                this.tabKeys.model += 1
            } else if (this.tab === 'LOOPS') {
                this.tabKeys.loops += 1
            } else if (this.tab === 'CONFORMANCE') {
                this.tabKeys.conformance += 1
            } else if (this.tab === 'ATTRS') {
                this.tabKeys.attrs += 1
            } else if (this.tab === 'COMPARISON') {
                this.tabKeys.comparison += 1
            } else if (this.tab === 'DASHBOARD') {
                this.tabKeys.dashboard += 1
            }
        },

        async getModelInfoDisplayed({ loopUid, variantCount, variantUid }) {
            this.settedChart = false
            this.disableElements = true
            if (loopUid === undefined && !variantCount && !variantUid) {
                this.displayedInfo = EMPTY_MODEL_INFO
                this.settedChart = true
                this.disableElements = false
                return
            }
            const { processId } = this.$route.params
            const params = this.buildVisualizationParams({
                ...(loopUid !== undefined ? { loopUid } : {}),
                ...(variantCount ? { variantCount } : {}),
                ...(variantUid ? { variantUid } : {}),
            })
            this.currentFilters = params.filters
            apiRequest(Api().visualizations.modelInfo({ processId, params }))
                .then((data) => {
                    this.displayedInfo = data
                })
                .catch(notifyError)
                .finally(() => {
                    this.settedChart = true
                    if (!this.variantsLoading) {
                        this.disableElements = false
                    }
                })
        },
        saveBpmnModel() {
            this.$refs.modelModal.open()
        },
        async generateModel({ modelName }) {
            this.$refs.modelModal.close()
            const arcs = (this.chart?.arcs || []).map(({
                source, target, sourceId, targetId, ...rest
            }) => {
                const normalizedSource = sourceId !== undefined ? sourceId : source
                const normalizedTarget = targetId !== undefined ? targetId : target
                return {
                    ...rest,
                    source: normalizedSource,
                    target: normalizedTarget,
                    sourceId: normalizedSource,
                    targetId: normalizedTarget,
                }
            })
            const modelData = {
                name: modelName,
                projectUid: this.$route.params.projectId,
                datasetUid: this.processId,
                activities: this.chart.activities,
                arcs,
            }
            apiRequest(Api().models.create({
                params: modelData,
            }))
                .then(() => {
                    notifySuccess(this.$t('visualization.bpmnOK'))
                })
                .catch(notifyError)
                .finally()
        },
        handleExportImage(format) {
            this.format = format.value
            this.isExportingFile = true
            const { chart } = this.$refs
            if (chart) {
                chart.downloadGraph(format)
                this.isExportingFile = false
            }
        },
        setPercentageMode(mode) {
            this.percentageDisplay = mode
            this.emitDisplayStateChanged()
        },
        setDirectionMode(mode) {
            this.directionDisplay = mode
            this.model.directionValue = this.directionDisplay.value
            this.emitDisplayStateChanged()
        },
        setStatsMode(mode) {
            this.statsDisplay = mode
            this.model.statsValue = this.statsDisplay.value
            this.emitDisplayStateChanged()
        },
        setHeatmapMode(mode) {
            this.heatmapModeDisplay = mode
            this.model.heatmapMode = mode
            this.emitDisplayStateChanged()
        },
        updateCostPerHour(value) {
            this.costPerHour = Number(value)
        },
        updateCurrency(value) {
            this.currency = value
        },
        async getSettings() {
            try {
                const params = {
                    datasetId: this.$route.params.processId,
                }
                const { data } = await apiRequest(Api().datasets.get(params));
                [this.settings] = data
                if (data[0].cost) {
                    this.currency = { value: data[0].cost.unit }
                    this.costPerHour = data[0].cost.value
                }
                this.loading = false
            } catch (error) {
                notifyError(error)
            }
        },
        changeOptionPanel1(newOption) {
            this.attributePanelOption = newOption
        },
        changeOptionPanel2(newOption) {
            this.frequencyPanelOption = newOption
        },
        loadingControl(load) {
            this.variantsLoading = load
            if (this.settedChart && !this.variantsLoading) {
                this.disableElements = false
            }
            if (this.variantsLoading) {
                this.disableElements = true
            }
        },
        handleChangeVariantTab(newVariantTab) {
            this.variantTab = newVariantTab
        },
        toggleFab(fab) {
            if (this.activeFab === fab) {
                this.$refs[fab].hide()
                this.activeFab = null
            } else {
                this.$refs[fab].show()
                if (this.activeFab) {
                    this.$refs[this.activeFab].hide()
                }
                this.activeFab = fab
            }
        },
        viewTrace(a) {
            const currentParams = { ...this.$route.params }
            currentParams.selectedCaseId = a.selectedCaseId
            currentParams.tab = a.tab
            this.tab = a.tab
            this.$router.push({ name: this.$route.name, params: currentParams })
        },
        checkCaseCompletion() {
            if (this.settings.caseCompletion === null) {
                return false
            }
            if (this.settings.caseCompletion.startingActivities.length === 0
                && this.settings.caseCompletion.occurringActivities.length === 0
                && this.settings.caseCompletion.endingActivities.length === 0) {
                return false
            }
            return true
        },
        toggleInspector() {
            if (!this.hasInspectorInfo && !this.hasInspectorPlots) return
            const next = !this.inspectorOpen
            this.inspectorOpen = next
            if (next) {
                if (this.inspectorTab === 'LOOP' && this.tab !== 'LOOPS') {
                    this.inspectorTab = 'MODEL'
                }
                if (!this.hasInspectorInfo && this.hasInspectorPlots) {
                    this.inspectorTab = 'ATTRIBUTE'
                }
                if (!this.hasInspectorPlots && this.hasInspectorInfo && ['ATTRIBUTE', 'FREQANDDUR', 'DURATIONMETER'].includes(this.inspectorTab)) {
                    this.inspectorTab = 'MODEL'
                }
            }
        },
        openInspectorTab(tabName) {
            const isInfoTab = ['MODEL', 'SLA', 'LOOP'].includes(tabName)
            const isPlotTab = ['ATTRIBUTE', 'FREQANDDUR', 'DURATIONMETER'].includes(tabName)

            if (isInfoTab && !this.hasInspectorInfo) return
            if (isPlotTab && !this.hasInspectorPlots) return
            if (tabName === 'LOOP' && this.tab !== 'LOOPS') return

            this.inspectorTab = tabName
            this.inspectorOpen = true
        },
        handleClickOutside(event) {
            if (this.activeFab) {
                const fabElement = this.$refs[this.activeFab]?.$el
                if (fabElement && !fabElement.contains(event.target)) {
                    this.$refs[this.activeFab].hide()
                    this.activeFab = null
                }
            }

            this.fabs.forEach((fab) => {
                if (fab !== this.activeFab) {
                    const fabElement = this.$refs[fab]?.$el
                    if (fabElement && !fabElement.contains(event.target)) {
                        this.$refs[fab].hide()
                    }
                }
            })

        },

        handleFrequencyIndicatorStateChanged(state) {
            if (!state || typeof state !== 'object') return
            this.frequencyIndicatorState = {
                ...this.frequencyIndicatorState,
                ...state,
            }
            this.emitFrequencyIndicatorStateChanged()
        },

        handleIncreaseFrequencyIndicator() {
            this.$refs.chart?.increaseFrequencyIndicator?.()
        },

        handleDecreaseFrequencyIndicator() {
            this.$refs.chart?.decreaseFrequencyIndicator?.()
        },

        handleZoomStateChanged(state) {
            if (!state || typeof state !== 'object') return
            this.canIncreaseZoom = !!state.canIncrease
            this.canDecreaseZoom = !!state.canDecrease
        },

        handleIncreaseZoom() {
            this.$refs.chart?.increaseZoom?.()
        },
        handleDecreaseZoom() {
            this.$refs.chart?.decreaseZoom?.()
        },

    },
}

</script>
<style scoped lang="scss">
$options-width: 340px;
$visualization-sidebar-width: 310px;

.Visualization {
    overflow: hidden;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    // QPage in ProcessLayout has padding: 25px (top) + 25px (bottom).
    // Use explicit viewport-based sizing so the graph never ends up with height: 0,
    // while still fitting between the global header and footer.
    height: calc(100vh - $header-height - $footer-height - 52px);
    min-height: 400px; // Just in case, to avoid collapsing when the viewport is too small.
    border-radius: 5px;
}
.Wrapper {
    flex: 1 1 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;

    & > .FixedBar {
            background: $primary;
            padding: 5px;
            border-radius: 5px;
        }
}

.ChargingHeight {
    flex: 1 1 auto;
    min-height: 0;
    flex-wrap: nowrap;
}
.Graph {
    height: 100%;
    flex: 1 1 auto;
    min-height: 0;
}

.GraphArea {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
    display: flex;
    flex-direction: column;
}
.ChartContainer {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    background-color: #ffffff;
    align-items: stretch;
    position: relative;
    .Actions {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 5;
        &:deep(.WButton) {
            padding: 7px 10px;
        }
    }
    .EmptyModel {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #fff;
        z-index: 10;
        text-align: center;
        font-size: 1.2em;
        color: #555;
    }
}

.Inspector {
    flex: 0 0 auto;
    width: 100%;
    border-top: 1px solid $gray-05;
    background: $white;
}

.InspectorBar {
    padding: 6px 10px;
}

.InspectorCompactGroup {
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
    padding: 2px 4px;
    cursor: pointer;
    user-select: none;
}

.InspectorCompactGroup :deep(.text-caption) {
    font-size: 0.8rem;
    line-height: 1.1rem;
}

.InspectorCompactGroup:focus {
    outline: none;
}

.InspectorCompactGroup:hover {
    background: $gray-bg;
}

.InspectorCompactGroup.is-disabled {
    cursor: default;
    opacity: 0.6;
}

.InspectorCompactGroup.is-disabled:hover {
    background: transparent;
}

.InspectorBody {
    max-height: 35vh;
    overflow: auto;
}
#view-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

.scroll-container {
    width: 100%;
    overflow-y: auto;
    display: grid;
    grid-template-columns: 1fr;
    justify-items: center;
}
.Options {
    width: $options-width;
    transition: $transition;
    z-index: 1100;
}
.q-tab-panels {
    height: 100%;
}

.Tabs {
    margin: 0;
    border-bottom: 1px solid $gray-05;
    background-color: $white;
    z-index:1100;
    &:deep(.Container) {
        padding: 0;
        .Tabs {
            display: flex;
            .Tab {
                width: fit-content;
                min-height: inherit;
                padding: 15px;
            }
        }
    }
}
.Panel {
    padding: 3px;
}
.BtnCreateZoomer {
    padding: 0;
    :deep(> span:nth-child(2)) {
        padding-left: 0.285em;
        min-height: 2.5em;
        padding-right: 0.4em;
    }
}
.BtnZoomer {
    :deep(> span:nth-child(2)) {
        gap: 10px;
    }
}

.ZoomControlsOverlay {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 6;
}
</style>
